/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { memo, useMemo } from 'react';

// eslint-disable-next-line no-unused-vars -- `jsx` is required by the classic pragma above
import { cssMap, jsx } from '@compiled/react';
import { useInView } from 'react-intersection-observer';

import { token } from '@atlaskit/tokens';

import { useSyntaxHighlighter } from '../hooks/use-syntax-highlighter';
import type { SupportedLanguage } from '../supported-languages';
import type { Decoration } from '../types/decoration';
import type { LineNumberRenderInfo } from '../types/line-number';

import { CodeLine, type ThemeName } from './code-line';

const styles = cssMap({
	pre: {
		margin: 0,
		overflowX: 'auto',
		color: token('color.text'),
		// We want the small body font's weight and 12px size...
		font: token('font.body.small'),
		// ...but the "code" font family
		fontFamily: token('font.family.code'),
		// Bump from 16px to 20px (1.25rem) for code readability
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '1.25rem',
		tabSize: 4,
	},
});

/**
 * Descendant selectors are unavoidable in both themes below: the `.token.*`
 * class names are emitted by refractor/Prism onto nodes this component renders
 * from highlighter output, so there is no element to co-locate the styles on.
 * `@atlaskit/code` themes Prism output the same way.
 */
/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors -- see above */
const themes = cssMap({
	/**
	 * Bitbucket's Prism syntax-highlighting theme. Translated from
	 * `prism-bitbucket.css` (the canonical theme in
	 * `@atlassian/bitkit-code` / `src/components/code/src/components/`)
	 * to use tokens instead of CSS variables
	 *
	 * Frontbucket-only `--ds-comment-refreshed` is replaced with
	 * `color.text.subtle` (canonical ADS "secondary text" color,
	 * matching the Frontbucket value to within a few hex points).
	 */
	bitbucket: {
		backgroundColor: token('elevation.surface'),
		'& .token.keyword, & .token.boolean, & .token.entity, & .token.important': {
			color: token('color.text.accent.red'),
		},
		'& .token.keyword.nil, & .token.hexcode.color, & .token.attr-value, & .token.constant, & .token.string':
			{
				color: token('color.text.accent.orange'),
			},
		'& .token.symbol, & .token.comment, & .token.prolog, & .token.doctype, & .token.cdata': {
			color: token('color.text.subtle'),
		},
		'& .token.url': {
			color: token('color.link'),
		},
		'& .token.number, & .token.unit': {
			color: token('color.text.accent.blue'),
		},
		'& .token.selector, & .token.tag': {
			color: token('color.text.brand'),
		},
		'& .token.atrule, & .token.property, & .token.property-access, & .token.attr-name, & .token.class-name, & .token.function':
			{
				color: token('color.text.accent.green'),
			},
		'& .token.variable, & .token.builtin': {
			color: token('color.text.accent.blue.bolder'),
		},
		'& .token.title, & .token.punctuation, & .token.operator, & .token.directive-hash, & .token.directive.keyword, & .token.macro-name, & .token.macro.string, & .token.doctype':
			{
				color: token('color.text'),
			},
		'& .token.regex, & .token.regex.class-name, & .token.regex.char-class, & .token.regex.number, & .token.regex.language-regex, & .token.regex.function, & .token.regex.punctuation, & .token.regex.operator':
			{
				color: token('color.text.accent.orange'),
			},
		'& .token.bold': {
			/*
			 * Frontbucket needed an `as 'bold'` cast here because its `cssMap`
			 * typing rejected `token()`'s `var(--ds-...)` string. AFM's
			 * @compiled/react version accepts it, so the cast is dropped —
			 * which also keeps `ui-styling-standard/no-unsafe-values` happy.
			 */
			fontWeight: token('font.weight.bold'),
		},
		'& .token.italic': {
			fontStyle: 'italic',
		},
		'& .token.inserted': {
			color: token('color.text.success'),
		},
		'& .token.deleted': {
			color: token('color.text.danger'),
		},
	},
	/**
	 * `@atlaskit/code`'s colour assignments, mirrored so a `CodeBlock` can sit
	 * next to an ADS `CodeBlock` without the two syntax themes disagreeing.
	 * Ported from the `.token` block in
	 * `platform/packages/design-system/code/src/code-block.tsx`.
	 *
	 * Colours only, by design: ADS additionally renders comments italic in
	 * `SFMono-MediumItalic` and keywords in `bolder`, which are deliberately
	 * not copied here so this variant is a like-for-like swap of `bitbucket`.
	 *
	 * Upstream orders these rules with an `&&&&…` specificity ladder because a
	 * single Prism span can carry two type classes (e.g. `token string url`)
	 * that ADS colours differently. This variant keeps the flat selector style
	 * the `bitbucket` theme already uses, so such spans resolve by source order
	 * rather than by that ladder — the rules below are therefore listed in
	 * reverse ladder order (lowest upstream rank first) so the intended winner
	 * comes last. Single-class spans, which is the overwhelming majority, match
	 * ADS exactly either way.
	 *
	 * ADS resolves both its `--ds--code--bg-color` and
	 * `--ds--code--line-number-bg-color` custom properties to
	 * `color.background.neutral` by default, so the gutter and the source share
	 * one background with no divider between them. `CodeLine` applies the
	 * matching gutter treatment.
	 */
	ads: {
		backgroundColor: token('color.background.neutral'),
		'& .token.key, & .token.keyword': {
			color: token('color.text.accent.blue'),
		},
		'& .token.attr-name': {
			color: token('color.text.accent.teal'),
		},
		'& .token.selector': {
			color: token('color.text.accent.blue'),
		},
		'& .token.comment, & .token.block-comment': {
			color: token('color.text.subtlest'),
		},
		'& .token.function-name': {
			color: token('color.text.accent.purple'),
		},
		'& .token.doctype': {
			color: token('color.text.accent.yellow'),
		},
		'& .token.substr': {
			color: token('color.text.subtlest'),
		},
		'& .token.code, & .token.bullet, & .token.entity, & .token.builtin, & .token.namespace': {
			color: token('color.text.accent.blue'),
		},
		'& .token.variable, & .token.symbol, & .token.regex': {
			color: token('color.text.accent.teal'),
		},
		'& .token.url': {
			color: token('color.text.accent.purple'),
		},
		'& .token.tag, & .token.quote, & .token.type, & .token.selector-pseudo, & .token.selector-attr':
			{
				color: token('color.text.accent.teal'),
			},
		'& .token.string': {
			color: token('color.text.accent.green'),
		},
		'& .token.section, & .token.title, & .token.class-name': {
			color: token('color.text.accent.purple'),
		},
		'& .token.meta-keyword': {
			color: token('color.text.accent.green'),
		},
		'& .token.meta': {
			color: token('color.text.subtlest'),
		},
		'& .token.function': {
			color: token('color.text'),
		},
		'& .token.number': {
			color: token('color.text.accent.blue'),
		},
		'& .token.attr-value': {
			color: token('color.text.accent.teal'),
		},
		'& .token.prolog': {
			color: token('color.text.accent.blue'),
		},
		'& .token.cdata': {
			color: token('color.text.subtlest'),
		},
		'& .token.punctuation': {
			color: token('color.text'),
		},
		'& .token.property': {
			color: token('color.text.accent.purple'),
		},
		'& .token.constant': {
			color: token('color.text.accent.teal'),
		},
		'& .token.deleted': {
			color: token('color.text.accent.red'),
		},
		'& .token.boolean': {
			color: token('color.text.accent.blue'),
		},
		'& .token.char': {
			color: token('color.text'),
		},
		'& .token.inserted': {
			color: token('color.text.accent.green'),
		},
		'& .token.operator': {
			color: token('color.text'),
		},
		'& .token.atrule': {
			color: token('color.text.accent.green'),
		},
		'& .token.important': {
			color: token('color.text.accent.yellow'),
		},
	},
});
/* eslint-enable @atlaskit/ui-styling-standard/no-nested-selectors */

type CodeBlockProps = {
	/**
	 * The source code string to render.
	 */
	code: string;

	/**
	 * The line number to display for the first line. Defaults to 1.
	 *
	 * Use this when rendering a snippet that represents a fragment of a
	 * larger file (e.g. lines 100-110 of a long source file). Values less
	 * than 1 are clamped to 1.
	 */
	firstLineNumber?: number;

	/**
	 * Customizes the rendering of each line number.
	 *
	 * The component owns the line-number cell, including its layout, sticky
	 * positioning, gutter width, and visual styling. The renderer receives both
	 * the displayed `lineNumber` and `styledLineNumber`, the package's default
	 * styled content. Wrap `styledLineNumber` to preserve its local styling, or
	 * ignore it to provide custom content within the same gutter cell.
	 *
	 * Note on accessibility: when this prop is not provided, the line-number
	 * cell has `aria-hidden="true"` so line numbers remain decorative. Providing
	 * a renderer removes that attribute because the consumer may add an
	 * interactive element that must remain perceivable by assistive technology.
	 * A purely visual custom renderer can keep its output decorative by adding
	 * `aria-hidden="true"` to the returned content.
	 *
	 * To make line numbers interactive while preserving their code font:
	 *
	 *   renderLineNumber={({ lineNumber, styledLineNumber }) => (
	 *     <a href={`#L${lineNumber}`}>{styledLineNumber}</a>
	 *   )}
	 *
	 * To replace the presentation completely, ignore `styledLineNumber`:
	 *
	 *   renderLineNumber={({ lineNumber }) => `Line ${lineNumber}`}
	 */
	renderLineNumber?: (info: LineNumberRenderInfo) => ReactNode;

	/**
	 * Identifier of the language to syntax-highlight the source as.
	 *
	 * When omitted (or set to a value that isn't reflected by SupportedLanguage),
	 * the source is rendered as plain text.
	 */
	language?: SupportedLanguage;

	/**
	 * Controls when syntax highlighting is loaded and applied.
	 *
	 * - `'lazy'` (default): the highlighter and its grammar are loaded
	 *   only when the block scrolls into the viewport
	 * - `'eager'`: load and apply highlighting as soon as the component
	 *   mounts
	 *
	 * Has no effect when `language` is not set.
	 */
	highlightStrategy?: 'lazy' | 'eager';

	/**
	 * Per-line, character-range decorations to apply on top of syntax
	 * highlighting. Each decoration targets a single line (`lineNumber`
	 * is the 1-based visible line number) and a character range within
	 * that line. Decorations
	 * whose range is empty after clamping to the line's text are
	 * silently skipped.
	 *
	 * Decorations are non-interactive: they style their target range
	 * via `className`, `style`, and/or `tagName`, but cannot host
	 * event handlers. See the `Decoration` type for details.
	 */
	decorations?: Decoration[];

	/**
	 * Which visual theme to render with. Defaults to `'ads'`.
	 *
	 * `'ads'` matches `@atlaskit/code`'s Prism token colours and its
	 * `color.background.neutral` background, across both the source and the
	 * line-number gutter. It does not change the block's border radius, block
	 * padding, or font, so it alone will not make this component
	 * pixel-identical to an ADS `CodeBlock`.
	 *
	 * Pass `'bitbucket'` to keep the theme this directory was ported with.
	 */
	theme?: ThemeName;

	/**
	 * Add a `data-testid` to the outer `<pre>`.
	 *
	 * Useful for disambiguating multiple `CodeBlock` instances on the same
	 * page in tests. Omitted when not provided.
	 */
	testId?: string;
};

/**
 * Subset of `CodeBlockProps` that a component composing several `CodeBlock`s
 * together (e.g. `MultiChunkFile`) would want to expose to its own
 * consumers unchanged, since each `CodeBlock` in the group should share
 * the same rendering options.
 */
export type GroupCodeBlockProps = Pick<
	CodeBlockProps,
	'renderLineNumber' | 'language' | 'highlightStrategy' | 'decorations' | 'theme'
>;

export const CodeBlock: React.MemoExoticComponent<(props: CodeBlockProps) => React.JSX.Element> =
	memo(
		({
			code,
			firstLineNumber = 1,
			language,
			highlightStrategy = 'lazy',
			renderLineNumber,
			decorations,
			theme = 'ads',
			testId,
		}: CodeBlockProps): React.JSX.Element => {
			const isEager = highlightStrategy === 'eager';

			const { ref: viewportRef, inView } = useInView({
				triggerOnce: true,
				skip: isEager,
			});

			const { lines } = useSyntaxHighlighter({
				code,
				language,
				disabled: !isEager && !inView,
			});

			const startingLineNumber = firstLineNumber > 0 ? firstLineNumber : 1;
			const lastLineNumber = startingLineNumber + lines.length - 1;

			const gutterDefaultWidth = useMemo(
				() => `${String(lastLineNumber).length}ch`,
				[lastLineNumber],
			);

			// Custom property names are not part of `CSSProperties`, so we explicitly cast here
			const preStyle = {
				'--bbc-code-line-number-width-default': gutterDefaultWidth,
			} as CSSProperties;

			const isLineNumberAriaHidden = renderLineNumber === undefined ? true : undefined;

			const lastIndex = lines.length - 1;

			const decorationsByLine = useMemo(() => {
				const buckets = new Map<number, Decoration[]>();
				for (const decoration of decorations ?? []) {
					const existing = buckets.get(decoration.lineNumber);
					if (existing) {
						existing.push(decoration);
					} else {
						buckets.set(decoration.lineNumber, [decoration]);
					}
				}
				return buckets;
			}, [decorations]);

			return (
				<pre
					css={[styles.pre, themes[theme]]}
					data-testid={testId}
					ref={viewportRef}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- sets a runtime-computed CSS custom property (gutter width), which cannot be expressed via `cssMap`
					style={preStyle}
				>
					<code>
						{lines.map((line, index) => {
							const lineNumber = startingLineNumber + index;
							return (
								<CodeLine
									key={index}
									line={line}
									lineNumber={lineNumber}
									decorations={decorationsByLine.get(lineNumber)}
									isFirst={index === 0}
									isLast={index === lastIndex}
									isLineNumberAriaHidden={isLineNumberAriaHidden}
									renderLineNumber={renderLineNumber}
									theme={theme}
								/>
							);
						})}
					</code>
				</pre>
			);
		},
	);
